import {ApolloProvider, InMemoryCache, ApolloClient, ApolloLink, Observable, createHttpLink, gql} from "@apollo/client";
import {getMainDefinition} from "@apollo/client/utilities";
import React, {FC, ReactNode} from "react";
import {Modal, notification} from "antd";
import fetch from "unfetch";
import LocalResolvers from "./LocalResolver";
import {ORGANIZATION_ID} from "../../const";
import {getApiBaseUrl} from "../../helpers/apiBaseUrl";
import {URL_LOGIN, URL_ACCOUNT, URL_INSTRUCTOR, URL_HOME, URL_LEGAL_NOTICE, URL_PRIVACY_POLICY} from "../../models/url";
import {captureGraphQLError} from "../../helpers/sentry";
import {correlationLink} from "../../helpers/reportContext";
import {ownMutationLink} from "../../helpers/useLiveRefresh";
import * as Sentry from "@sentry/react";

type ApolloWrapperProps = {
    children: ReactNode;
};

// Dynamische API-URL basierend auf Umgebung (Entwicklung: Webpack-Proxy leitet weiter)
const uri = `${getApiBaseUrl()}/graphql`;

const cache = new InMemoryCache({
    // Apollo automatically generates keys for the cache by concatenating `__typename` and `id`.
    // If we disable the typenames, Apollo can not generate proper cache keys, which results in failed automatic
    // cache updates. This can be fixed in our case, since we use UUIDs as our ids, which means we won’t have the
    // same id for different types, which is why we can use the id as the cache key for an entity.
    //
    // The future of __typename is still under discussion at the time of writing. See the issue for more info:
    // https://github.com/apollographql/apollo-feature-requests/issues/6
    addTypename: false,
    // WICHTIG: false für ALLE Objekte zurückgeben = keine Normalisierung
    // Mit addTypename: false kann Apollo die Objekte beim Lesen nicht richtig rekonstruieren,
    // weil es nicht weiß welche Felder erwartet werden. Daher: keine Cache-Normalisierung.
    dataIdFromObject: () => false,
});

// THIS DOES NOT WORK!!!
// Problem here is that the cache keys are not unique and the cache will not be updated
// const cache = new InMemoryCache({
//     addTypename: true, // Default aktivieren
//     dataIdFromObject: ({id, __typename}) => (id ? `${__typename}:${id}` : null),
// });

const LOCAL_STATE_INIT = gql`
    query LocalStateInit {
        LocalState @client {
            createCourse {
                locationId
            }
            bookAttendees {
                selectedAttendee {
                    id
                    firstname
                    bookedCourseIds
                }
                selectedCourseId
            }
        }
    }
`;

// inital data
cache.writeQuery({
    query: LOCAL_STATE_INIT,
    data: {
        LocalState: {
            createCourse: {
                locationId: null,
            },
            bookAttendees: {
                selectedAttendee: {
                    id: null,
                    firstname: null,
                    bookedCourseIds: null,
                },
                selectedCourseId: null,
            },
        },
    },
});

// Globaler Error Handler für Netzwerkfehler mit Retry
const MAX_RETRIES = 3;
const MODAL_DEBOUNCE_TIME = 5000; // 5 Sekunden zwischen Modals

// State Management für Modal mit Timestamp-basiertem Debouncing
const networkErrorModalState = {
    lastShownTimestamp: 0,
    isCurrentlyShowing: false,
    resetTimeout: null as NodeJS.Timeout | null,
};

const shouldShowNetworkErrorModal = (): boolean => {
    // Atomare Check-and-Set Operation um Race Conditions zu vermeiden
    // Wir prüfen und setzen in einem einzigen if-Block
    const now = Date.now();

    // Erste Check: Ist bereits ein Modal sichtbar?
    if (networkErrorModalState.isCurrentlyShowing) {
        return false;
    }

    // Zweite Check: Zeit seit letztem Modal
    const timeSinceLastModal = now - networkErrorModalState.lastShownTimestamp;

    if (timeSinceLastModal < MODAL_DEBOUNCE_TIME) {
        return false;
    }

    // Beide Checks bestanden - SOFORT State setzen um Race Condition zu vermeiden
    // Dies muss VOR dem return true passieren!
    networkErrorModalState.isCurrentlyShowing = true;
    networkErrorModalState.lastShownTimestamp = now;

    // Auto-Reset nach 10 Sekunden für den Fall dass onOk/onCancel nicht aufgerufen werden
    if (networkErrorModalState.resetTimeout) {
        clearTimeout(networkErrorModalState.resetTimeout);
    }

    networkErrorModalState.resetTimeout = setTimeout(() => {
        networkErrorModalState.isCurrentlyShowing = false;
        networkErrorModalState.resetTimeout = null;
    }, 10000);

    return true;
};

const resetNetworkErrorModal = () => {
    networkErrorModalState.isCurrentlyShowing = false;

    if (networkErrorModalState.resetTimeout) {
        clearTimeout(networkErrorModalState.resetTimeout);
        networkErrorModalState.resetTimeout = null;
    }
};

// Auth-Error State: Verhindert Retry-Loops bei abgelaufener Session
let isAuthErrorRedirecting = false;

// Tracking ob die App bereits eine erfolgreiche Auth-Response erhalten hat.
// false = App wurde gerade frisch geladen (z.B. nach Browser-Neustart mit abgelaufenen Cookies)
// true = Mindestens eine GraphQL-Response kam ohne Auth-Error zurück → User war aktiv eingeloggt
let hasHadSuccessfulAuth = false;

const handleAuthError = () => {
    // Nur einmal ausführen
    if (isAuthErrorRedirecting) return;

    // Auf öffentlichen Seiten ist UNAUTHENTICATED erwartetes Verhalten
    // (AnnouncementBanner feuert GetActiveAnnouncements mit @Authorized auf JEDER Seite,
    // auch auf Seiten die kein Login erfordern)
    const path = window.location.pathname;
    if (path === URL_HOME
        || path.startsWith(URL_LEGAL_NOTICE)
        || path.startsWith(URL_PRIVACY_POLICY)
        || path.startsWith(URL_LOGIN)
        || path.startsWith(URL_ACCOUNT)
        || path.startsWith(URL_INSTRUCTOR)) return;

    isAuthErrorRedirecting = true;

    if (!hasHadSuccessfulAuth) {
        // App frisch geladen mit abgelaufenen Cookies → Silent Redirect zum Login (kein Modal)
        cache.reset()
            .catch(() => {})
            .then(() => {
                window.location.href = URL_LOGIN;
            });

        return;
    }

    // Session lief ab während aktiver Nutzung → Modal zeigen
    Modal.warning({
        title: "Anmeldung erforderlich",
        content: "Bitte melde dich erneut an.",
        okText: "Zum Login",
        closable: false,
        maskClosable: false,
        keyboard: false,
        onOk: () => {
            // Cache leeren und zum Login weiterleiten (auch bei Cache-Fehler redirecten)
            cache.reset()
                .catch(() => {})
                .then(() => {
                    window.location.href = URL_LOGIN;
                });
        },
    });
};

const getMaintenanceMessageFromError = (error: unknown): string | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyError = error as any;
    const networkError = anyError?.networkError;
    const statusCode = networkError?.statusCode ?? networkError?.status;
    const result = networkError?.result;

    if (statusCode !== 503) {
        return undefined;
    }

    if (result && typeof result === "object" && result.maintenance === true) {
        return typeof result.message === "string" && result.message.trim().length > 0 ? result.message : "Wartungsarbeiten – bitte versuche es später erneut.";
    }

    return undefined;
};

/**
 * Umbuchungen behandeln einen Verbindungsabbruch selbst („Ergebnis unklar“ im Wizard): Der erste Lauf
 * arbeitet auf dem Server weiter. Eine Wiederholung scheiterte an dessen Ergebnis, und der Dialog
 * „Neu laden“ verdeckte den Hinweis, nicht erneut auszuführen — beides entfällt für diese Operationen.
 */
const WIZARD_HANDLED_NETWORK_ERRORS = ["ExecuteTransfer", "ExecuteBulkTransfer"];

export const errorLink = new ApolloLink((operation, forward) => {
    // Rekursive Retry-Funktion mit Cleanup
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attemptRequest = (retryCount: number): Observable<any> => {
        return new Observable((observer) => {
            let retryTimeoutId: NodeJS.Timeout | null = null;
            let retrySubscription: {unsubscribe: () => void} | null = null;
            let isUnsubscribed = false;

            const subscription = forward(operation).subscribe({
                next: (result) => {
                    if (!isUnsubscribed) {
                        observer.next(result);
                    }
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                error: (error: any) => {
                    // Wenn bereits unsubscribed, ignoriere den Error
                    if (isUnsubscribed) {
                        return;
                    }

                    const maintenanceMessage = getMaintenanceMessageFromError(error);

                    if (maintenanceMessage) {
                        if (shouldShowNetworkErrorModal()) {
                            Modal.info({
                                title: "Wartungsarbeiten",
                                content: maintenanceMessage,
                                okText: "OK",
                                onOk: () => {
                                    resetNetworkErrorModal();
                                },
                                onCancel: () => {
                                    resetNetworkErrorModal();
                                },
                            });
                        }

                        // Nicht retryen bei Maintenance
                        observer.error(error);

                        return;
                    }

                    // Prüfe auf Netzwerkfehler
                    const isNetworkError = error?.networkError || error instanceof ProgressEvent || error?.message?.includes("Network error") || error?.message?.includes("Failed to fetch");

                    // Mutationen nie automatisch wiederholen (#1134): Bricht die Verbindung ab, nachdem der
                    // Server schon geschrieben hat, bucht die Wiederholung ein zweites Mal (Guthaben, Zahlung,
                    // Buchung). Der Dialog „Verbindungsfehler“ überlässt die Entscheidung dem Menschen.
                    const definition = getMainDefinition(operation.query);
                    const isMutation = definition.kind === "OperationDefinition" && definition.operation === "mutation";
                    // Lang laufende Queries ebenfalls ausnehmen; die Mutationen in der Liste sind durch
                    // isMutation schon abgedeckt und stehen nur noch zur Dokumentation da.
                    const noRetryOperations = ["ko9ExecuteImport", "ko9DryRun", "removeAllBookersOfTenant", "GetDuplicateBookers", "CreateBooker", "UpdateBooker", ...WIZARD_HANDLED_NETWORK_ERRORS];
                    const skipRetry = isMutation || noRetryOperations.indexOf(operation.operationName) !== -1;
                    const handledByWizard = WIZARD_HANDLED_NETWORK_ERRORS.indexOf(operation.operationName) !== -1;

                    if (isNetworkError && retryCount < MAX_RETRIES && !skipRetry) {
                        // Retry: Erhöhe Counter und versuche erneut
                        console.log(`[Apollo Retry] Versuch ${retryCount + 1}/${MAX_RETRIES} für ${operation.operationName}`);

                        // Warte kurz vor dem Retry (exponential backoff: 500ms, 1s, 2s)
                        const delay = Math.min(500 * 2 ** retryCount, 2000);

                        retryTimeoutId = setTimeout(() => {
                            // Prüfe nochmal ob unsubscribed, bevor Retry gestartet wird
                            if (isUnsubscribed) {
                                return;
                            }

                            // Rekursiver Aufruf mit erhöhtem Counter
                            retrySubscription = attemptRequest(retryCount + 1).subscribe({
                                next: (result) => {
                                    if (!isUnsubscribed) {
                                        observer.next(result);
                                    }
                                },
                                error: (err) => {
                                    if (!isUnsubscribed) {
                                        observer.error(err);
                                    }
                                },
                                complete: () => {
                                    if (!isUnsubscribed) {
                                        observer.complete();
                                    }
                                },
                            });
                        }, delay);

                        return; // Wichtig: observer.error NICHT aufrufen!
                    }

                    // Max Retries erreicht oder kein Netzwerkfehler
                    if (isNetworkError) {
                        // Thread-safe Modal Anzeige mit Debouncing
                        if (!handledByWizard && shouldShowNetworkErrorModal()) {
                            Modal.error({
                                title: "Verbindungsfehler",
                                content: "Es besteht keine Verbindung zum Server. Bitte überprüfe deine Internetverbindung oder versuche es später erneut.",
                                okText: "Neu laden",
                                onOk: () => {
                                    resetNetworkErrorModal();
                                    window.location.reload();
                                },
                                onCancel: () => {
                                    resetNetworkErrorModal();
                                },
                            });
                        }

                        console.error("[Apollo Error] Max Retries erreicht:", error);
                        Sentry.withScope((scope) => {
                            scope.setTag("graphql_operation", operation.operationName);
                            scope.setExtra("retry_count", MAX_RETRIES);
                            Sentry.captureException(error);
                        });
                    } else {
                        console.error("[Apollo Error]:", error);
                    }

                    // Fehler weiterreichen
                    observer.error(error);
                },
                complete: () => {
                    if (!isUnsubscribed) {
                        observer.complete();
                    }
                },
            });

            // Cleanup-Handler: Wichtig für Memory Leaks und Observable Contract
            return () => {
                isUnsubscribed = true;

                // Cancel pending retry timeout
                if (retryTimeoutId) {
                    clearTimeout(retryTimeoutId);
                    retryTimeoutId = null;
                }

                // Unsubscribe original subscription
                subscription.unsubscribe();

                // Unsubscribe retry subscription if exists
                if (retrySubscription) {
                    retrySubscription.unsubscribe();
                    retrySubscription = null;
                }
            };
        });
    };

    return attemptRequest(0);
});

// #709: Bereiche, die eine Sichtbarkeitsregel auf „Kein Zugriff“ stellt, lehnt der Server mit dieser
// Meldung ab. Lesende Abfragen dorthin sind erwartbar: erzwungene Refetches der Rechnungslisten nach
// Buchungsaktionen ({query: GetUnsettledBookingsDocument} lädt auch ohne offene Liste), Auswahllisten
// in Formularen. Sie gelten als leeres Ergebnis ohne Fehler: kein Hinweis-Toast, und eine erfolgreiche
// Mutation mit awaitRefetchQueries scheitert nicht nachträglich am Refetch. Mutationen bleiben Fehler.
const HIDDEN_AREA_DENIAL = /^Authorization denied: (invoices|staff|locations|courseTypes|interestLists) area not visible \(staff visibility rules\)$/;
// #1213: Dasselbe für lesende Abfragen, die der Rollen-Guard (@Authorized) abweist. Seiten der Admin-App
// laden Admin-Abfragen (Abzeichen-Dialog, Abrechnungsdetails, Kursarten, Kundenakte) auch für Kursleitungen;
// der Server lehnt korrekt ab, rund 200 rote Toasts pro Woche sind trotzdem falsch. Das SECURITY-Log auf dem
// Server bleibt; Mutationen bleiben Fehler mit Toast.
const ROLE_DENIAL = "Authorization denied: missing required role(s)";
const isSilentQueryDenial = (message: string): boolean => HIDDEN_AREA_DENIAL.test(message) || message === ROLE_DENIAL;

// Exportiert für Tests
export const createAuthErrorLink = (
    onAuthError: () => void,
    onAuthSuccess: () => void,
    isRedirecting: () => boolean,
) => {
    return new ApolloLink((operation, forward) => {
        // Wenn bereits ein Auth-Error-Redirect läuft, Request nicht absenden
        if (isRedirecting()) {
            return new Observable((observer) => {
                observer.complete();
            });
        }

        return forward(operation).map((response) => {
            // Prüfe ob GraphQL-Errors einen UNAUTHENTICATED-Code enthalten
            const hasAuthError = response.errors?.some(
                (error) => error.extensions?.code === "UNAUTHENTICATED"
            );

            if (hasAuthError) {
                onAuthError();
            } else if (!response.errors || response.errors.length === 0) {
                // Nur bei komplett fehlerfreien Responses als "erfolgreich authentifiziert" werten.
                // Responses mit anderen Errors (z.B. FORBIDDEN) könnten von Public-Queries stammen.
                onAuthSuccess();
            }

            // FORBIDDEN umfasst zwei Faelle:
            //   1) Rollen-Guard (@Authorized): User hat nicht die noetige Rolle.
            //   2) Business-Guard (z.B. Tombstone-/Mollie-Guard): Aktion durch Server-Logik blockiert.
            // Kein Redirect zum Login (verhindert Auth-Loop). Wenn der Server eine Message
            // mitschickt, zeigen wir genau diese — sonst Fallback auf den generischen Rollen-Text.
            const forbiddenError = response.errors?.find(
                (error) => error.extensions?.code === "FORBIDDEN"
            );

            if (forbiddenError) {
                console.warn(`[Apollo] FORBIDDEN error in ${operation.operationName}: ${forbiddenError.message}`);
            }

            const isHiddenAreaRead =
                Boolean(response.errors?.length) &&
                (response.errors ?? []).every((error) => error.extensions?.code === "FORBIDDEN" && isSilentQueryDenial(error.message)) &&
                (getMainDefinition(operation.query) as {operation?: string}).operation === "query";

            if (isHiddenAreaRead) {
                return {...response, errors: undefined};
            }

            if (forbiddenError) {
                const serverMessage = forbiddenError.message?.trim();
                notification.warning({
                    message: "Aktion nicht möglich",
                    description: serverMessage && serverMessage.length > 0
                        ? serverMessage
                        : "Du hast nicht die nötigen Berechtigungen für diese Aktion.",
                    duration: 6,
                });
            }

            return response;
        });
    });
};

const sentryLoggingLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
        if (response.errors && response.errors.length > 0) {
            captureGraphQLError(
                response.errors,
                operation.operationName,
                operation.variables,
            );
        }
        return response;
    });
});

const authErrorLink = createAuthErrorLink(
    handleAuthError,
    () => { hasHadSuccessfulAuth = true; },
    () => isAuthErrorRedirecting,
);

const httpLink = createHttpLink({
    fetch: fetch as unknown as typeof window.fetch,
    uri,
    headers: {
        "x-ko-organization-id": ORGANIZATION_ID,
        "x-application-type": "admin-app",
        // "CSRF-Token": document
        //     .querySelector('meta[name="csrf-token"]')
        //     ?.getAttribute("content"),
    },
    credentials: "include",
});

export const apolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, authErrorLink, sentryLoggingLink, ownMutationLink, correlationLink, httpLink]),
    cache,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolvers: LocalResolvers as any,
});

export const URIContext = React.createContext("");

const ApolloWrapper: FC<ApolloWrapperProps> = ({children}) => {
    return (
        <URIContext.Provider value={uri}>
            <ApolloProvider client={apolloClient}>
                {children}
            </ApolloProvider>
        </URIContext.Provider>
    );
};

export default ApolloWrapper;
